import * as React from 'react';
import BackgroundImage from 'gatsby-background-image';
import {
  Flex,
  Heading,
  Input,
  Stack,
  Text,
  Button,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { graphql, useStaticQuery, navigate } from 'gatsby';
import { convertToBgImage } from 'gbimage-bridge';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import EmailIcon from '../../../assets/email-icon.svg';
import LocationIcon from '../../../assets/location.svg';
import { useMixpanel } from 'gatsby-plugin-mixpanel';

type Data = {
  heroImage: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData;
    };
  };
};

type ContactUsProps = {
  headingText: string;
  bodyText: string;
};

export const ContactUs: React.FC<ContactUsProps> = ({
  headingText,
  bodyText,
}) => {
  const { heroImage } = useStaticQuery<Data>(
    graphql`
      {
        heroImage: file(relativePath: { eq: "hero-background.png" }) {
          childImageSharp {
            gatsbyImageData(
              quality: 90
              layout: FULL_WIDTH
              placeholder: BLURRED
              formats: [AUTO, WEBP, AVIF]
            )
          }
        }
      }
    `,
  );

  const [status, setStatus] = React.useState('idle')
  const [errorMessage, setErrorMessage] = React.useState(null)
  const forminitRef = React.useRef(null)
  const formRef = React.useRef(null)
  const formId = '3tcg1rx1gq1'

  React.useEffect(() => {
    if (typeof window !== 'undefined' && !window.Forminit) {
      const script = document.createElement('script')
      script.src = 'https://forminit.com/sdk/v1/forminit.js'
      script.onload = () => {
        forminitRef.current = new window.Forminit()
      }
      document.body.appendChild(script)
    } else if (window.Forminit) {
      forminitRef.current = new window.Forminit()
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!forminitRef.current) {
      setStatus('error')
      setErrorMessage('Form not ready. Please try again.')
      return
    }

    setStatus('loading')
    setErrorMessage(null)

    const formData = new FormData(formRef.current)
    const { data, error } = await forminitRef.current.submit(formId, formData)

    if (error) {
      setStatus('error')
      setErrorMessage(error.message)
      return
    }

    setStatus('success')
    formRef.current.reset()
    navigate('/thank-you')
  }


  const bgImage = convertToBgImage(heroImage.childImageSharp.gatsbyImageData);

  const mixpanel = useMixpanel();

  return (
    <BackgroundImage
      Tag="section"
      id="contact_section"
      {...bgImage}
      style={{
        width: '100%',
        backgroundPosition: 'bottom center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        paddingBottom: '60px',
      }}
      fadeIn
    >
      <Flex direction="column" as="section" align="center" p={10}>
        <Heading size="lg" textAlign="center" px={4} pb={5}>
          {headingText}
        </Heading>
        <Heading fontSize="1.8em" textAlign="center" px={4}>
          {bodyText}
        </Heading>
      </Flex>
      <Flex justifyContent="space-around" px={{ md: '200px' }} wrap="wrap">
        <Stack spacing={4} px={4} pb={5}>
          <Flex align="flex-start">
            <EmailIcon width={30} />
            <Text fontSize="xl" px={5} textColor="brand.blue" fontWeight="bold">
              hello@canyou.com.au
            </Text>
          </Flex>
          <Flex align="flex-start">
            <LocationIcon width={30} />
            <Text fontSize="xl" px={5} textColor="brand.blue" fontWeight="bold">
              55 Pyrmont Bridge Rd,
              <br /> Pyrmont NSW 2009
            </Text>
          </Flex>
        </Stack>
        <form ref={formRef} onSubmit={handleSubmit} id="contact-form">
          <Stack spacing={{ base: 2, md: 3 }} minW={{ md: '400px' }}>
            <FormControl id="firstName" isRequired>
              <FormLabel fontWeight="bold" htmlFor="firstName">
                First name
              </FormLabel>
              <Input
                borderColor="#99A2C0"
                placeholder="John"
                id="firstName"
                name="fi-sender-firstName"
                bgColor="white"
              />
            </FormControl>
            <FormControl id="lastName" isRequired>
              <FormLabel fontWeight="bold" htmlFor="lastName">
                Last name
              </FormLabel>
              <Input
                borderColor="#99A2C0"
                placeholder="Example"
                id="lastName"
                name="fi-sender-lastName"
                bgColor="white"
              />
            </FormControl>
            <FormControl id="email" isRequired>
              <FormLabel fontWeight="bold" htmlFor="email">
                Email address
              </FormLabel>
              <Input
                type="email"
                id="email"
                name="fi-sender-email"
                placeholder="john@example.com"
                borderColor="#99A2C0"
                bgColor="white"
              />
            </FormControl>
            <FormControl id="phoneNumber" isRequired>
              <FormLabel fontWeight="bold" htmlFor="phoneNumber">
                Phone number
              </FormLabel>
              <Input
                borderColor="#99A2C0"
                bgColor="white"
                placeholder="0400 999 999"
                required
                type="tel"
                id="phoneNumber"
                name="fi-text-phone"
              />
            </FormControl>
            <FormControl id="businessName">
              <FormLabel fontWeight="bold" htmlFor="businessName">
                Business name
              </FormLabel>
              <Input
                borderColor="#99A2C0"
                bgColor="white"
                placeholder="Jones and Co"
                id="businessName"
                name="fi-text-businessName"
              />
            </FormControl>
          </Stack>
          <Flex justify={{ base: 'center', md: 'flex-start' }}>
            <Button
              mt={6}
              width="190px"
              type="submit"
              onClick={(): void => {
                mixpanel.track('Homepage: Submitted Contact Form');
              }}
            >
              Submit
            </Button>
          </Flex>
        </form>
      </Flex>
    </BackgroundImage>
  );
};
